import { buildEmailTemplateQuillKeyboardBindings } from '../../../core/quill/pm-email-template-token-quill.util';

/** HTML helpers shared by the Template Library editors (Email and Document templates): preview with sample values, plain-text fallback, empty-Quill check. */

/** Sample values for template preview (Settings → Template Library view / preview). */
const TEMPLATE_LIBRARY_PREVIEW_SAMPLES: Readonly<Record<string, string>> = {
  user: 'John Doe',
  username: 'John Doe',
  client: 'Acme Trading Ltd',
  clientname: 'Acme Trading Ltd',
  'client.name': 'Acme Trading Ltd',
  companyname: 'Acme Trading Ltd',
  customer: 'Alex Morgan',
  tasknumber: 'TSK-1042',
  taskname: 'Annual accounts preparation',
  taskdate: '22 May 2026',
  accountantname: 'Jane Smith',
  firmname: 'Capium Practice Ltd',
  accountantemail: 'jane.smith@capium.example',
  accountantnumber: '+44 20 7946 0958',
  invoiceamount: '£1,250.00',
  invoicenumber: 'INV-2026-0142',
  duedate: '30 Jun 2026',
  deadlinename: 'Corporation tax payment',
  hoursspent: '12.5',
  'practice.name': 'Capium Practice Ltd',
  practicename: 'Capium Practice Ltd',
  'proposal.price_summary':
    'Bookkeeping — £120/month\nPayroll — £85/month\nYear-end accounts — £450',
  'proposal.commencement_date': '1 Apr 2026',
  'proposal.end_date': '31 Mar 2027',
  companyid: 'CMP-10042',
  accountsofficeref: 'AO-77821',
  email: 'accounts@acmetrading.example',
  companytype: 'Limited',
  address: '10 High Street, London, EC1A 1BB',
  phone: '+44 20 7946 0958',
  website: 'https://www.acmetrading.example',
  registrationno: '12345678',
  businessstartdate: '1 Jan 2020',
  bookstartdate: '6 Apr 2025',
  utrno: '1234567890',
  yearend: '31 Mar 2026',
  vatscheme: 'Standard',
  vatsubmittype: 'Quarterly',
  vatregno: 'GB123456789',
  vatregdate: '1 Apr 2021',
  hmrchofficeno: 'HMRC-001',
  payerefno: 'PAYE-445566',
  contactfirstname: 'Alex',
  contactmiddlename: 'J',
  contactlastname: 'Morgan',
  contactphonenumber: '+44 7700 900123',
  contactemail: 'alex.morgan@acmetrading.example',
  servicedetails: 'Annual accounts and corporation tax return',
  servicepricedetails: 'Fixed fee £2,400 plus VAT',
  servicestart: '1 Apr 2026',
  serviceend: '31 Mar 2027',
  sender: 'Jane Smith',
  'insert link here': 'https://portal.example.com/sign/agreement'
};

export function templateLibraryLooksLikeHtml(content: string): boolean {
  return /<(?:p|br|div|span|table|ul|ol|li|h[1-6]|strong|em|b|i|u|a)\b/i.test(content);
}

export function templateLibraryPreviewSampleForToken(tokenInner: string): string {
  const key = tokenInner.trim().toLowerCase();
  const mapped = TEMPLATE_LIBRARY_PREVIEW_SAMPLES[key];
  if (mapped) {
    return mapped;
  }
  const label = tokenInner.trim().replace(/[._]/g, ' ');
  if (/email/i.test(label)) {
    return 'contact@example.com';
  }
  if (/phone|number|mobile/i.test(label)) {
    return '+44 20 7946 0958';
  }
  if (/date/i.test(label)) {
    return '22 May 2026';
  }
  if (/\bid\b/i.test(label) || label.endsWith('ID') || label.endsWith('Id')) {
    return 'SMP-10042';
  }
  if (/name/i.test(label)) {
    return `Sample ${label}`;
  }
  return `Sample ${label}`;
}

export function templateLibraryApplyPreviewPlaceholders(raw: string): string {
  if (!raw?.trim()) {
    return '';
  }
  return raw.replace(/\[([^\]]+)\]/g, (_match, inner: string) =>
    templateLibraryPreviewSampleForToken(String(inner ?? ''))
  );
}

export function templateLibraryPreparePreviewHtml(raw: string): string {
  const withSamples = templateLibraryApplyPreviewPlaceholders(raw);
  if (!withSamples.trim()) {
    return '';
  }
  if (templateLibraryLooksLikeHtml(withSamples)) {
    return withSamples;
  }
  const escaped = withSamples
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
  return escaped.replace(/\r\n/g, '\n').replace(/\n/g, '<br>');
}

/** Treat empty Quill output (`<p><br></p>`) as blank before save. */
export function normalizeTemplateLibraryQuillHtml(html: string): string {
  const trimmed = (html ?? '').trim();
  if (!trimmed) {
    return '';
  }
  const plain = trimmed
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim();
  if (!plain) {
    return '';
  }
  return trimmed;
}

/** Template body as HTML for the raw view: HTML is kept as-is, plain text is escaped with line breaks. */
export function templateLibraryRawToHtml(raw: string): string {
  if (!raw?.trim()) return '';
  if (templateLibraryLooksLikeHtml(raw)) {
    return raw;
  }
  const escaped = raw
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
  return escaped.replace(/\r\n/g, '\n').replace(/\n/g, '<br>');
}

/** Quill config for the Template Library editors: atomic token embeds, Backspace/Delete removes a whole token.
 *  A fresh object per call, so each editor host keeps its own copy (as before the split). */
export function templateLibraryQuillModules() {
  return {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      [{ font: [] }, { size: [] }],
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ align: [] }],
      ['link']
    ],
    keyboard: {
      bindings: buildEmailTemplateQuillKeyboardBindings()
    }
  };
}
